import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useUpdateEffect } from "react-use";
import { AutoSizer } from "react-virtualized";
import { useVirtualizer } from "@tanstack/react-virtual";
import moment from "moment";
import PropTypes from "prop-types";
import useLocalStorage from "react-use/lib/useLocalStorage";
import { MySubtaskContainer } from "~/components/MyWork/MyTasks/MySubtaskContainer";
import { UserTaskGroupedByDateListItem } from "~/components/MyWork/MyTasks/UserTaskGroupedByDateListItem";
import UserWorkListHeader from "~/components/MyWork/MyTasks/UserWorkListHeader";
import { UserWorkTaskItem } from "~/components/MyWork/MyTasks/UserWorkTaskItem";
import { editPersonalizedConfigOptions } from "~/store/actions/actions.personalizedConfigOptions";
import {
  getMyWorkCollapsedTaskDateGroups,
  getPersonalizedConfigOption,
} from "~/store/selectors/selector.configOptions";
import { getTasksGroupedByDateForVirtualization } from "~/store/selectors/selector.userTasks.jsx";
import { MyTasksDelighter } from "../MyTasksDelighter.jsx";
import { UserTasksEmptySlate } from "../UserTasksEmptySlate.jsx";

export const UserTaskGroupedByDateList = ({ haveAssignments, userId, setHasDelighter }) => {
  const parentRef = useRef();
  const today = useMemo(() => {
    return moment();
  }, []);
  const dispatch = useDispatch();
  const collapsedDateGroupsFromConfig = useSelector((state) => {
    if (userId) {
      return [];
    }
    return getMyWorkCollapsedTaskDateGroups(state);
  });
  const [hasScrollbar, setHasScrollbar] = useState(false);
  const [collapsedDateGroups, setCollapsedDateGroups] = useState(collapsedDateGroupsFromConfig);
  const groupBy = useSelector((state) =>
    getPersonalizedConfigOption(state, userId ? "user_profile_tasks_group_by" : "my_work_tasks_grouped_by")
  );
  const dataFromSelector = useSelector((state) => getTasksGroupedByDateForVirtualization(state, groupBy, userId));

  const data = useMemo(() => {
    return dataFromSelector.filter((i) => {
      if (i.type === "date") {
        return true;
      }

      if (i.type === "task" || i.type === "subtask") {
        return !collapsedDateGroups.includes(i.object.collection);
      }
    });
  }, [collapsedDateGroups, dataFromSelector]);

  const hasTasks = dataFromSelector.some((item) => item.type !== "date");
  const [timestamp, setTimestamp] = useLocalStorage("my-work-delighter-timestamp", hasTasks ? today.unix() : 0);
  const showDelighter = useMemo(() => {
    return !userId && moment.unix(timestamp).isSame(today, "day");
  }, [timestamp, today, userId]);

  useEffect(() => {
    setHasDelighter(showDelighter && !hasTasks);
    if (!showDelighter && hasTasks) {
      setTimestamp(today.unix());
    }
  }, [today, showDelighter, setTimestamp, hasTasks, setHasDelighter]);

  const handleDismiss = useCallback(() => {
    setTimestamp(0);
  }, [setTimestamp]);

  const handleCollapsedDateGroups = useCallback((name) => {
    setCollapsedDateGroups((prevState) =>
      prevState.includes(name) ? prevState.filter((p) => p !== name) : [...prevState, name]
    );
  }, []);

  useUpdateEffect(() => {
    if (!userId) {
      dispatch(
        editPersonalizedConfigOptions({
          my_work_collapsed_tasks_date_groups: collapsedDateGroups,
        })
      );
    }
  }, [dispatch, collapsedDateGroups, userId]);

  const resolveCount = useCallback(
    (name) => {
      const assignments = dataFromSelector.filter((item) => {
        const type = item.type;
        if (type !== "date") {
          return item.object.collection === name;
        }
      }, {});

      return assignments.length;
    },
    [dataFromSelector]
  );

  const renderItem = useCallback(
    (item) => {
      switch (item.type) {
        case "date": {
          return (
            <div className="my_work_tasks_grouped_by_date">
              <UserTaskGroupedByDateListItem
                collection={data}
                collectionName={item.name}
                count={resolveCount(item.name)}
                isExpanded={!collapsedDateGroups.includes(item.name)}
                setExpanded={() => handleCollapsedDateGroups(item.name)}
              />
            </div>
          );
        }
        case "task": {
          return (
            <div className="tasks_group_list">
              <UserWorkTaskItem
                id={item.object.id}
                userId={userId}
                showProjectName
              />
            </div>
          );
        }
        case "subtask": {
          return (
            <div className="tasks_group_list">
              <div className="list-item-wrapper">
                <MySubtaskContainer
                  id={item.object.id}
                  className="tw-rounded-none tw-pl-6"
                  isAssigneeHidden
                  isProjectNameVisible
                />
              </div>
            </div>
          );
        }
        default:
          return null;
      }
    },
    [collapsedDateGroups, data, handleCollapsedDateGroups, resolveCount, userId]
  );

  const rowVirtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 40,
    overscan: 25,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  useEffect(() => {
    if (virtualItems.length) {
      setHasScrollbar(rowVirtualizer.getTotalSize() > rowVirtualizer.getSize());
    }
  }, [rowVirtualizer, virtualItems.length]);

  return (
    <>
      {showDelighter && !hasTasks ? (
        <MyTasksDelighter
          className="tw-mb-4"
          onDismiss={handleDismiss}
        />
      ) : null}
      {showDelighter && !hasTasks && !haveAssignments ? null : (
        <>
          {haveAssignments ? (
            <>
              <UserWorkListHeader
                userId={userId}
                hasScrollbar={hasScrollbar}
              />
              <AutoSizer>
                {({ height, width }) => (
                  <div
                    ref={parentRef}
                    style={{
                      width,
                      height,
                      overflowY: "auto",
                    }}
                  >
                    <div
                      style={{
                        height: rowVirtualizer.getTotalSize(),
                        width: "100%",
                        position: "relative",
                      }}
                    >
                      {virtualItems.map((virtualRow) => {
                        const item = data[virtualRow.index];
                        return (
                          <div
                            key={item.type === "date" ? item.name : `${item.type}-${item.object.id}`}
                            ref={rowVirtualizer.measureElement}
                            data-index={virtualRow.index}
                            style={{
                              position: "absolute",
                              width: "100%",
                              transform: `translateY(${virtualRow.start}px)`,
                            }}
                          >
                            {renderItem(item)}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </AutoSizer>
            </>
          ) : (
            <UserTasksEmptySlate userId={userId} />
          )}
        </>
      )}
    </>
  );
};

UserTaskGroupedByDateList.propTypes = {
  haveAssignments: PropTypes.bool,
  userId: PropTypes.number,
  setHasDelighter: PropTypes.func,
};

UserTaskGroupedByDateList.defaultProps = {
  setHasDelighter: () => null,
};
